/**
 * useLocale — 当前活跃 UI locale（en/es/pt/zh），与全局语言选择器对齐。
 *
 * 取自 react-i18next 的 `i18n.language`（真相源：语言选择器 `@/i18n/useLocale.setLocale`
 * 会 changeLanguage + 持久化 novaLocale + 同步 redux preferredLocale）。归一到 AppLocale，
 * 非受支持语言（如带区域码 zh-CN，或 fr）按前缀回退、最终回退 'en'。切语言时本 hook
 * 随 useTranslation 自动重渲染。
 *
 * 用于在 i18n map（`{en, es, pt, zh}`）中选择展示语言（配合 pickI18n）。
 */
import { useTranslation } from 'react-i18next'
import { type AppLocale, isAppLocale } from '@/i18n/locales'

export type Locale = AppLocale

export function useLocale(): Locale {
  const { i18n } = useTranslation()
  const lng = i18n.language
  if (isAppLocale(lng)) return lng
  const prefix = typeof lng === 'string' ? lng.split('-')[0] : undefined
  return isAppLocale(prefix) ? prefix : 'en'
}

/**
 * pickI18n — 从 `{en: ..., zh: ..., ...}` map 中按 locale 取展示值。回退顺序（严格）：
 *   1. map[locale]（请求的语言译文）
 *   2. map.en（i18n map 里的英文覆盖）
 *   3. fallback（调用方传入的英文基名/规范名，如菜单 `name`、KB base title）
 *
 * ⚠️ **绝不返回「未请求的其它语言」**。曾有旧逻辑在 1/2 落空时回退到 map 里「第一个非空值」，
 * 当 i18n map 只含非英文译文（如 `{es,pt,zh}` 无 `en`）、locale=en 时会抓到某外语，导致英语模式
 * 显示葡/西文（侧栏菜单 + KB 均实测踩到）。英文源始终在 `map.en` 或 fallback(规范 base)，缺译就回退到它。
 * 无 fallback 的调用方（activity 模板）自己有上层兜底，拿到空串即走它们的 fallback，不会被塞错语言。
 */
export function pickI18n(
  map: Record<string, string> | null | undefined,
  locale: string,
  fallback = '',
): string {
  if (!map) return fallback
  if (typeof map[locale] === 'string' && map[locale].length > 0) return map[locale]
  if (typeof map.en === 'string' && map.en.length > 0) return map.en
  return fallback
}
